import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'

const GOOGLE_FONTS = [
  'auto', 'Roboto', 'Open Sans', 'Montserrat', 'Lato', 'Poppins',
  'Oswald', 'Raleway', 'Playfair Display', 'Merriweather', 'Nunito',
  'Inter', 'Bebas Neue', 'Archivo', 'DM Sans', 'Space Grotesk',
]

export function OverlayTextEditor({ overlayText, onChange }: { overlayText: string; onChange: (v: string) => void }) {
  const { t } = useTranslation()
  return (
    <div>
      <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
        {t('amazonListing.slots.overlayTextLabel')}
      </label>
      <textarea
        value={overlayText}
        onChange={e => onChange(e.target.value)}
        rows={2}
        placeholder={t('amazonListing.slots.overlayTextPlaceholder')}
        className="w-full text-sm p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 resize-none"
      />
    </div>
  )
}

export function InfoOverlayEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation()
  return (
    <div>
      <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
        {t('amazonListing.slots.infoOverlayLabel')}
      </label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={2}
        placeholder={t('amazonListing.slots.infoOverlayPlaceholder')}
        className="w-full text-sm p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 resize-none"
      />
      <p className="text-2xs text-neutral-400 mt-0.5">{t('amazonListing.slots.infoOverlayHint')}</p>
    </div>
  )
}

export function FontSelector({ fontFamily, onChange }: { fontFamily: string; onChange: (v: string) => void }) {
  const { t } = useTranslation()
  return (
    <div>
      <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
        {t('amazonListing.slots.fontFamily')}
      </label>
      <select
        value={fontFamily}
        onChange={e => onChange(e.target.value)}
        className="w-full text-sm p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800"
        style={fontFamily !== 'auto' ? { fontFamily } : undefined}
      >
        {GOOGLE_FONTS.map(f => (
          <option key={f} value={f} style={f !== 'auto' ? { fontFamily: f } : undefined}>
            {f === 'auto' ? t('amazonListing.slots.fontAuto') : f}
          </option>
        ))}
      </select>
    </div>
  )
}

export function PromptViewer({ prompt }: { prompt: string }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const preview = prompt.slice(0, 150)
  const needsExpand = prompt.length > 150

  return (
    <div>
      <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
        {t('amazonListing.slots.promptLabel')}
      </label>
      <div className="text-xs font-mono p-2 rounded-lg bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap break-words">
        {expanded ? prompt : preview}{!expanded && needsExpand && '...'}
      </div>
      {needsExpand && (
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-2xs text-orange-500 mt-1">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? t('amazonListing.slots.showLess') : t('amazonListing.slots.showMore')}
        </button>
      )}
    </div>
  )
}

export function SlotSettings({ resolution, aspectRatio, onResolutionChange, onAspectRatioChange }: {
  resolution: string; aspectRatio: string; onResolutionChange: (v: string) => void; onAspectRatioChange: (v: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          {t('amazonListing.slots.resolution')}
        </label>
        <select value={resolution} onChange={e => onResolutionChange(e.target.value)}
          className="w-full text-sm p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800">
          <option value="1024">1K (1024)</option>
          <option value="2048">2K (2048)</option>
          <option value="4096">4K (4096)</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          {t('amazonListing.slots.aspectRatio')}
        </label>
        <select value={aspectRatio} onChange={e => onAspectRatioChange(e.target.value)}
          className="w-full text-sm p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800">
          <option value="1:1">1:1</option>
          <option value="4:5">4:5</option>
          <option value="16:9">16:9</option>
        </select>
      </div>
    </div>
  )
}
